import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { todayStr } from "../../lib/availability.js";
import { Badge, Button, Field, Input } from "../../lib/ui.js";
import { SectionCard, errorStyle, linkButtonStyle, mutedStyle } from "./common.js";
import { formatWeekdayDateOnly } from "../../lib/datetime.js";

interface NoticeRow {
  id: string;
  message: string;
  starts_on: string | null;
  ends_on: string | null;
  closes_business: boolean;
}

function dateLabel(d: string): string {
  return formatWeekdayDateOnly(d);
}

function rangeLabel(n: NoticeRow): string {
  if (n.starts_on && n.ends_on) return n.starts_on === n.ends_on ? dateLabel(n.starts_on) : `${dateLabel(n.starts_on)} to ${dateLabel(n.ends_on)}`;
  if (n.starts_on) return `From ${dateLabel(n.starts_on)}`;
  if (n.ends_on) return `Until ${dateLabel(n.ends_on)}`;
  return "Until removed";
}

/** Temporary notices: a message the receptionist gives priority to while it applies, and optionally a closure
 *  that stops bookings for those dates on both the phone and the web (a business-wide holiday in the booking
 *  rules; existing bookings are not touched). */
export function NoticesSection() {
  const { activeBusinessId } = useAuth();
  const [rows, setRows] = useState<NoticeRow[]>([]);
  const [message, setMessage] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [closes, setCloses] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    if (!activeBusinessId) return;
    const { data } = await supabase
      .from("business_notices")
      .select("id, message, starts_on, ends_on, closes_business")
      .eq("business_id", activeBusinessId)
      .order("created_at", { ascending: false });
    setRows((data ?? []) as NoticeRow[]);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    if (closes && (!startsOn || !endsOn)) {
      setError("Closing the business needs a first and last day.");
      return;
    }
    if (startsOn && endsOn && endsOn < startsOn) {
      setError("The last day can't be before the first day.");
      return;
    }
    setSaving(true);
    const { error: err } = await supabase.rpc("rpc_portal_save_notice", {
      p_business_id: activeBusinessId,
      p_message: message,
      p_starts_on: startsOn || null,
      p_ends_on: endsOn || null,
      p_closes_business: closes,
    });
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setMessage("");
    setStartsOn("");
    setEndsOn("");
    setCloses(false);
    await load();
  }

  async function handleDelete(id: string) {
    setError(null);
    const { error: err } = await supabase.rpc("rpc_portal_delete_notice", { p_notice_id: id });
    if (err) {
      setError(err.message);
      return;
    }
    await load();
  }

  const today = todayStr();
  const status = (n: NoticeRow): { label: string; tone: "success" | "neutral" | "warning" } => {
    if (n.starts_on && today < n.starts_on) return { label: "Upcoming", tone: "warning" };
    if (n.ends_on && today > n.ends_on) return { label: "Ended", tone: "neutral" };
    return { label: "Active now", tone: "success" };
  };

  return (
    <SectionCard
      title="Temporary notes"
      description="Holidays, closures or a change of plan. While a note applies, the receptionist treats it as more important than anything else it knows. Tick the box to also stop new bookings on those days, on the phone and on the web."
    >
      <form onSubmit={handleAdd} style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 560 }}>
        <Field label="Note for the receptionist">
          <textarea
            required
            maxLength={500}
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Closed Thursday 24 to Saturday 26 December for Christmas. Back Monday."
            style={{ width: "100%", boxSizing: "border-box", padding: 8, fontFamily: "var(--rc-font)", fontSize: 13.5, borderRadius: "var(--rc-radius-md)", border: "1px solid var(--rc-border-strong)" }}
          />
        </Field>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Field label="First day (optional)">
            <Input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} style={{ width: 170 }} />
          </Field>
          <Field label="Last day (optional)">
            <Input type="date" value={endsOn} min={startsOn || undefined} onChange={(e) => setEndsOn(e.target.value)} style={{ width: 170 }} />
          </Field>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5 }}>
          <input type="checkbox" checked={closes} onChange={(e) => setCloses(e.target.checked)} />
          Business closed on these days: no new bookings (existing bookings are not cancelled)
        </label>
        <div>
          <Button type="submit" variant="primary" disabled={saving || !message.trim()}>
            {saving ? "Saving..." : "Add note"}
          </Button>
        </div>
      </form>
      {error && <p style={errorStyle}>{error}</p>}

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((n) => {
          const st = status(n);
          return (
            <div key={n.id} style={{ border: "1px solid var(--rc-border)", borderRadius: "var(--rc-radius-md)", padding: "10px 12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div style={{ fontSize: 13.5, color: "var(--rc-text)" }}>{n.message}</div>
                <button onClick={() => void handleDelete(n.id)} style={{ ...linkButtonStyle, color: "var(--rc-danger)", flexShrink: 0 }}>
                  Remove
                </button>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, fontSize: 12, color: "var(--rc-text-muted)" }}>
                <Badge tone={st.tone}>{st.label}</Badge>
                {n.closes_business && <Badge tone="danger">Bookings closed</Badge>}
                <span>{rangeLabel(n)}</span>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <span style={mutedStyle}>No temporary notes.</span>}
      </div>
    </SectionCard>
  );
}
